/**
 * Utility functions for order entry
 */

/**
 * Pack three note fields into a single string
 * Format: 'daily_notes': {value} | 'weekly_notes': {value} | 'all_notes': {value}
 */
export const packNotes = (
  dailyNotes: string,
  weeklyNotes: string,
  allNotes: string,
): string => {
  return `'daily_notes': ${dailyNotes} | 'weekly_notes': ${weeklyNotes} | 'all_notes': ${allNotes}`;
};

/**
 * Unpack a note string into three separate fields
 * Format: 'daily_notes': {value} | 'weekly_notes': {value} | 'all_notes': {value}
 */
export const unpackNotes = (
  packedNotes: string | null | undefined,
): {
  daily_notes: string;
  weekly_notes: string;
  all_notes: string;
} => {
  const defaultResult = {
    daily_notes: "",
    weekly_notes: "",
    all_notes: "",
  };

  if (!packedNotes) {
    return defaultResult;
  }

  try {
    // Split by pipe delimiter
    const parts = packedNotes.split("|").map((p) => p.trim());

    const result: any = { ...defaultResult };

    parts.forEach((part) => {
      // Match pattern: 'field_name': value
      const match = part.match(/'([^']+)':\s*(.+)/);
      if (match) {
        const [, key, value] = match;
        if (
          key === "daily_notes" ||
          key === "weekly_notes" ||
          key === "all_notes"
        ) {
          result[key] = value.trim();
        }
      }
    });

    return result;
  } catch (error) {
    console.error("Error unpacking notes:", error);
    return defaultResult;
  }
};

/**
 * Convert order status to Japanese text
 */
export const getStatusInJapanese = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: "未確定",
    confirmed: "最終確定済み",
    waiting_final_confirmation: "最終確認待ち",
    processing: "処理中",
    shipped: "発送済み",
    delivered: "配達済み",
    cancelled: "キャンセル",
    completed: "完了",
  };

  return statusMap[status.toLowerCase()] || status;
};

export const getStatusBadgeColor = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: "default",
    confirmed: "green",
    waiting_final_confirmation: "orange",
    processing: "blue",
    shipped: "purple",
    delivered: "green",
    cancelled: "red",
    completed: "green",
  };

  return statusMap[status.toLowerCase()] || "default";
};

const getItemNames = (
  order: any,
  getter: (item: any) => string | undefined,
) => {
  const list = order?.item_list || order?.order_items;

  if (!list?.length) {
    return "-";
  }

  const values = list.map((item: any) => getter(item)?.trim()).filter(Boolean);

  return values.length > 0 ? values.join(", ") : "-";
};

export const getProductNames = (order: any) => {
  const list = order?.item_list || order?.order_items;

  if (!list?.length) {
    return "-";
  }

  const seenProducts = new Set<string>();
  const productNames = list
    .map((item: any) => {
      const productName = item.product_name?.trim();

      if (!productName) {
        return undefined;
      }

      const productCode = item.product_code?.trim();
      const productKey = productCode
        ? `code:${productCode}`
        : `name:${productName}`;

      if (seenProducts.has(productKey)) {
        return undefined;
      }

      seenProducts.add(productKey);
      return productName;
    })
    .filter(Boolean);

  return productNames.length > 0 ? productNames.join(", ") : "-";
};

export const getOriginNames = (order: any) => {
  return getItemNames(order, (item) => item.production || item.origin_name);
};
